import { MapMode } from '@pn/core/domain/map';
import { builtInLayers } from '@pn/services/map/mapbox/style';
import { elevationContours } from '@pn/services/map/mapbox/style/pn/elevationContours';
import { grids } from '@pn/services/map/mapbox/style/pn/grids';
import { gridLayerIds } from '@pn/services/map/mapbox/style/pnMapboxLayers';
import { clone, isNil, set } from 'lodash-es';
import type { MapboxIMap } from '..';

export const updateMode: MapboxIMap['updateMode'] = async function (
  this: MapboxIMap,
  newMode,
  grids,
  baseLayers
) {
  const style = this._native.getStyle();

  const updatedStyle = await updateStyleLayers(
    style,
    newMode,
    grids,
    baseLayers
  );

  this._native.setStyle(updatedStyle);
};

export async function updateStyleLayers(
  style: mapboxgl.Style,
  mapMode: MapMode,
  gridsConfig: Record<string, boolean>, // is set only during map initialization
  baseLayersConfig: Record<string, boolean> // is set only during map initialization
): Promise<mapboxgl.Style> {
  let newStyleLayers: mapboxgl.AnyLayer[] = [];

  switch (mapMode) {
    case MapMode.Standard: {
      newStyleLayers = (
        await import('@pn/services/map/mapbox/style/standardMapboxLayers')
      ).standardMapboxLayers;
      break;
    }
    case MapMode.Light: {
      newStyleLayers = (
        await import('@pn/services/map/mapbox/style/lightMapboxLayers')
      ).lightMapboxLayers;
      break;
    }
    case MapMode.Dark: {
      newStyleLayers = (
        await import('@pn/services/map/mapbox/style/darkMapboxLayers')
      ).darkMapboxLayers;
      break;
    }
    case MapMode.Satellite: {
      newStyleLayers = (
        await import('@pn/services/map/mapbox/style/satelliteMapboxLayers')
      ).satelliteMapboxLayers;
      break;
    }
    default:
      throw new Error('Invalid map mode');
  }

  const mode = [MapMode.Standard, MapMode.Light].includes(mapMode)
    ? 'light'
    : 'dark';

  const dynamicallyStyledLayers = new Map<string, mapboxgl.Layer>(
    [...elevationContours(mode), ...grids(mode)].map((layer) => [
      layer.id,
      layer as mapboxgl.Layer,
    ])
  );

  const gridLayerIdsToKeys: Record<string, string> = {};
  Object.entries(gridLayerIds).forEach(([key, layerIds]) => {
    layerIds.forEach((layerId) => {
      gridLayerIdsToKeys[layerId] = key;
    });
  });

  const updatedStyle = clone(style);

  for (let i = updatedStyle.layers.length - 1; i >= 0; i--) {
    const layer = updatedStyle.layers[i] as mapboxgl.Layer;

    /* Apply dynamic styles for grids and elevation contours */
    if (dynamicallyStyledLayers.has(layer.id)) {
      set(layer, 'paint', dynamicallyStyledLayers.get(layer.id)!.paint);
    }

    if (layer.id in gridLayerIdsToKeys) {
      const gridKey = gridLayerIdsToKeys[layer.id];
      const isVisible = gridsConfig?.[gridKey]; // undefined during mode updates
      if (!isNil(isVisible)) {
        set(layer, 'layout.visibility', isVisible ? 'visible' : 'none');
      }
    }

    /* Remove built-in layers to be replaced */
    if (!isNil(layer.metadata) && layer.metadata['pn:built-in']) {
      updatedStyle.layers.splice(i, 1);
    }
  }

  const baseLayerIdsToKeys: Record<string, string> = builtInLayers.reduce<
    Record<string, string>
  >((acc, config) => {
    config.sourceLayers.forEach((sourceLayerId) => {
      acc[sourceLayerId] = config.id;
    });
    return acc;
  }, {});

  for (let i = 0; i < newStyleLayers.length; i++) {
    const layer = newStyleLayers[i] as mapboxgl.Layer;

    if (
      !isNil(layer['source-layer']) &&
      layer['source-layer'] in baseLayerIdsToKeys
    ) {
      const baseLayerKey = baseLayerIdsToKeys[layer['source-layer']];
      const isVisible = baseLayersConfig?.[baseLayerKey];
      if (!isNil(isVisible)) {
        set(layer, 'layout.visibility', isVisible ? 'visible' : 'none');
      }
    }
  }

  updatedStyle.layers.unshift(...newStyleLayers);

  return updatedStyle;
}
